import { useEffect, useRef, useState } from "react";
import {
  X,
  Send,
  User,
  Sparkles,
  Plus,
  Check,
  Copy,
  RotateCcw,
  Thermometer,
  Leaf,
  Maximize2,
  History,
  Trash2Icon,
  Square,
  RocketIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { cn } from "../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Textarea } from "./ui/textarea";
import clsx from "clsx";
import {
  useChatThreads,
  useSearchThreads,
  useThreadMessages,
  useCreateChatThread,
  useSendChatMessage,
  useRegenerateChatMessage,
  useUpdateThreadTitle,
  useDeleteThread,
  useChatStream,
  ChatThread,
} from "../hooks/useChat";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const quickActions = [
  {
    icon: Thermometer,
    label: "Temperature trends",
    query: "What is the national temperature trends in Tanzania?",
  },
  {
    icon: Leaf,
    label: "Wheat Yield Gap",
    query: "What is the projected yield gap in wheats in Tanzania ?",
  },
  {
    icon: RocketIcon,
    label: "Emission Scenarios",
    query: "Explain Future Emission Scenarios in climate risk assessment.",
  },
];

export const AgentButton = ({ userName }: { userName: string }) => {
  const token =
    typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;

  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [input, setInput] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Local message state
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeThread, setActiveThread] = useState<ChatThread | null>(null);
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  // React query hooks
  const threadsQuery = useChatThreads(token ?? undefined, false);
  const searchThreadsQuery = useSearchThreads(token ?? undefined, searchQuery);
  const messagesQuery = useThreadMessages(
    token ?? undefined,
    activeThread?.uuid,
  );

  const createThreadMutation = useCreateChatThread(token ?? undefined);
  const sendMessageMutation = useSendChatMessage(token ?? undefined);
  const regenerateMessageMutation = useRegenerateChatMessage(
    token ?? undefined,
  );
  const updateThreadTitleMutation = useUpdateThreadTitle(token ?? undefined);
  const deleteThreadMutation = useDeleteThread(token ?? undefined);

  const streamState = useChatStream();

  const displayThreads = searchQuery
    ? (searchThreadsQuery.data ?? [])
    : (threadsQuery.threads ?? []);

  // Update greeting when user name changes
  useEffect(() => {
    if (messages.length === 1 && messages[0].id === "1") {
      setMessages([
        {
          id: "1",
          role: "assistant",
          content: `Hello ${userName ?? ""}! I'm your AI assistant. How can I help you today?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [userName]);

  // Sync messages when thread changes
  useEffect(() => {
    if (messagesQuery.messages && activeThread) {
      const chatMessages = messagesQuery.messages.map((m) => ({
        id: m.uuid,
        role: m.role as "user" | "assistant",
        content: m.content,
        timestamp: new Date(m.created_at),
      }));
      setMessages(chatMessages);
    }
  }, [messagesQuery.messages, activeThread]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamState.isStreaming]);

  // Handlers
  const handleNewConversation = () => {
    setMessages([]);
    setInput("");
    setActiveThread(null);
    setShowHistory(false);
  };

  const handleCopy = async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSelectThread = async (thread: ChatThread) => {
    if (activeThread?.uuid == thread?.uuid) return;

    setActiveThread(thread);
    setShowHistory(false);
    setSearchQuery("");
    handleCancelEditing();
  };

  const handleDeleteThread = async (threadUuid: string) => {
    const confirm = window.confirm(
      "Are you sure to delete this conversation ?",
    );
    if (!confirm) return;

    await deleteThreadMutation.mutateAsync({ threadUuid });
    if (activeThread?.uuid === threadUuid) {
      handleNewConversation();
    }
  };

  const handleStartEditingTitle = (thread: ChatThread) => {
    setEditingThreadId(thread.uuid);
    setEditingTitle(thread.title || "");
  };

  const handleSaveTitle = async () => {
    if (!editingThreadId || !editingTitle.trim()) return;

    try {
      await updateThreadTitleMutation.mutateAsync({
        threadUuid: editingThreadId,
        title: editingTitle.trim(),
      });
      setEditingThreadId(null);
      setEditingTitle("");
    } catch (error) {
      console.error("Error updating thread title:", error);
    }
  };

  const handleCancelEditing = () => {
    setEditingThreadId(null);
    setEditingTitle("");
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      handleCancelEditing();
    }
  };

  const handleSend = async (q?: string) => {
    const messageToSend = q || input.trim();
    if (!messageToSend || streamState.isStreaming || !token) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: messageToSend,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    try {
      let initResult;
      if (activeThread) {
        initResult = await sendMessageMutation.mutateAsync({
          threadUuid: activeThread.uuid,
          content: messageToSend,
        });
      } else {
        initResult = await createThreadMutation.mutateAsync({
          content: messageToSend,
        });
        if (initResult.thread) {
          setActiveThread(initResult.thread as ChatThread);
        }
      }

      // Create pending assistant message
      const assistantMessageId =
        initResult.assistant_message_id || Date.now().toString();
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      const isFirstMessage = !activeThread;

      // Start streaming
      streamState.startStream(
        initResult,
        (event) => {
          if (event.type === "token") {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? { ...msg, content: msg.content + event.text }
                  : msg,
              ),
            );
          } else if (event.type === "error") {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? { ...msg, content: `Error: ${event.message}` }
                  : msg,
              ),
            );
          }
        },
        (error) => {
          console.error("Stream error:", error);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content:
                      "Sorry, I encountered an error while processing your request.",
                  }
                : msg,
            ),
          );
        },
        isFirstMessage && initResult.thread?.uuid
          ? {
              pollTitleForThreadUuid: initResult.thread.uuid,
              token: token ?? undefined,
            }
          : undefined,
      );
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === (messages[messages.length - 1]?.id ?? "")
            ? {
                ...msg,
                content:
                  "Sorry, I encountered an error while processing your request.",
              }
            : msg,
        ),
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRegenerate = async (messageId: string) => {
    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex <= 0 || !activeThread || !token) return;

    let userMessageIndex = messageIndex - 1;
    while (
      userMessageIndex >= 0 &&
      messages[userMessageIndex].role !== "user"
    ) {
      userMessageIndex--;
    }

    if (userMessageIndex < 0) return;

    const userMessage = messages[userMessageIndex];

    try {
      const initResult = await regenerateMessageMutation.mutateAsync({
        threadUuid: activeThread.uuid,
        content: userMessage.content,
      });

      const newAssistantMessageId = initResult.assistant_message_id;
      const newAssistantMessage: Message = {
        id: newAssistantMessageId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };

      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === messageIndex ? newAssistantMessage : msg,
        ),
      );

      streamState.startStream(
        initResult,
        (event) => {
          if (event.type === "token") {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === newAssistantMessageId
                  ? { ...msg, content: msg.content + event.text }
                  : msg,
              ),
            );
          }
        },
        (error) => console.error("Regenerate stream error:", error),
      );
    } catch (error) {
      console.error("Error regenerating message:", error);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  const lastAssistantIndex = [...messages]
    .reverse()
    .findIndex((m) => m.role === "assistant");
  const lastAssistantMessageId =
    lastAssistantIndex !== -1
      ? messages[messages.length - 1 - lastAssistantIndex].id
      : null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isExpanded && (
        <div className="fixed inset-0 top-0 bottom-0 left-0 right-0 backdrop-blur-sm bg-white/30 z-40" />
      )}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                className={cn(
                  "h-14 w-14 md:w-18 md:h-18 rounded-full shadow-lg transition-all duration-300 hover:scale-105 z-50",
                  "bg-gradient-to-br from-lime-100 to-[#0C3A04] hover:from-[#4DC932] hover:to-[#0E4505]",
                  isOpen && "rotate-90",
                  // isExpanded && "hidden",
                )}
              >
                {isOpen ? (
                  <X className="size-5 md:size-7" />
                ) : (
                  <Sparkles className="size-5 md:size-7" />
                )}
                <span className="sr-only">Toggle AI Chat</span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Toggle AI Assistant</p>
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          side="top"
          align={"end"}
          sideOffset={isExpanded ? 4 : 16}
          className={clsx(
            "p-0 overflow-hidden rounded-2xl border-0 shadow-2xl z-[9999] flex flex-col gap-0",
            isExpanded
              ? "w-full md:w-[92vw] max-h-[86vh]"
              : "w-[95%] md:w-[540px]",
          )}
        >
          {/* Header */}
          <div className="flex justify-between bg-gradient-to-r from-lime-100 to-[#0C3A04] px-4 py-4 text-primary-foreground">
            <Button
              title="Toggle Expansion"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
              className="absolute -left-14 top-6 h-12 w-12 rounded-full shadow-lg text-white/80 hover:text-white bg-gradient-to-br from-lime-100 to-[#0C3A04] hover:from-[#4DC932] hover:to-[#0E4505]"
            >
              {/* <ExpandIcon className="h-4 w-4" /> */}
              <Maximize2 className="h-5 w-5 -rotate-90" />
              <span className="sr-only">Toggle Expansion</span>
            </Button>

            {isExpanded && (
              <Button
                title="Close Chat"
                size="icon"
                onClick={() => setIsOpen(!isOpen)}
                className="absolute -left-14 top-20 h-12 w-12 rounded-full shadow-lg text-white/80 hover:text-white bg-gradient-to-br from-lime-100 to-[#0C3A04] hover:from-[#4DC932] hover:to-[#0E4505]"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close Chat</span>
              </Button>
            )}

            {!isExpanded && (
              <Button
                title="Show History"
                size="icon"
                onClick={() => setShowHistory(!showHistory)}
                className="absolute -left-14 top-20 h-12 w-12 rounded-full shadow-lg text-white/80 hover:text-white bg-gradient-to-br from-lime-100 to-[#0C3A04] hover:from-[#4DC932] hover:to-[#0E4505]"
              >
                <History className="h-5 w-5" />
                <span className="sr-only">Toggle History</span>
              </Button>
            )}

            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-foreground/20">
                <Sparkles className="h-7 w-7" />
              </div>
              <div>
                <h3 className="font-semibold text-[16px]">AI Assistant</h3>
                <p className="text-xs text-primary-foreground/80">
                  Powered by historical climate data & model projections
                </p>
              </div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleNewConversation}
                  className="h-10 w-10 rounded-full text-white/80 hover:bg-white/20 hover:text-white"
                >
                  <Plus className="h-4 w-4" />
                  <span className="sr-only">New conversation</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>New conversation</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Chat Interface */}
          <div className={"flex flex-row w-full"}>
            {showHistory || isExpanded ? (
              <div
                className={clsx(
                  "flex flex-col gap-1 py-3 px-2",
                  isExpanded
                    ? "min-h-[65vh] w-96 bg-gray-100"
                    : "min-h-[611px] w-full",
                )}
              >
                <p className="text-lg font-bold py-5 px-3">
                  Recent Conversations
                </p>
                <div className="px-2 py-2">
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                {displayThreads.length === 0 &&
                  !threadsQuery.isLoading &&
                  !searchThreadsQuery.isLoading && (
                    <p className="text-sm text-gray-500 text-center py-4 italic">
                      No conversations yet...
                    </p>
                  )}

                {displayThreads.map((thread) => (
                  <div
                    key={thread.uuid}
                    className="flex items-center gap-2 px-2"
                  >
                    {editingThreadId === thread.uuid ? (
                      <div className="flex flex-row border border-lime-300 rounded items-center w-full">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) =>
                            setEditingTitle(e.target.value.slice(0, 50))
                          }
                          onKeyDown={handleTitleKeyDown}
                          onBlur={handleSaveTitle}
                          className="flex-1 px-3 py-2 text-sm h-11 rounded focus:outline-none focus:ring-2 focus:ring-lime-500"
                          autoFocus
                          maxLength={50}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEditing}
                          className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-500 rounded"
                        >
                          <X className="h-4 w-4 text-[inherit]" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        title={thread.title ?? "New conversation"}
                        variant="ghost"
                        onClick={() => handleSelectThread(thread)}
                        onDoubleClick={() => handleStartEditingTitle(thread)}
                        className={clsx(
                          "block flex-1 justify-start text-left text-sm h-11 truncate",
                          activeThread?.uuid === thread.uuid
                            ? "bg-lime-100 text-white hover:bg-green-100 hover:text-white"
                            : "hover:bg-gray-200",
                        )}
                      >
                        {thread.title || "New conversation"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteThread(thread.uuid)}

                      className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-500 hover:bg-red-500/20">

                      <Trash2Icon className="h-4 w-4 text-[inherit]" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : undefined}
            {!showHistory || isExpanded ? (
              <div className={"flex flex-col gap-0 p-0 w-full"}>
                {/* Quick Actions - only show when few messages */}
                {messages.length <= 2 && !activeThread && (
                  <div
                    className={clsx(
                      "border-b bg-muted/20",
                      isExpanded ? "px-6 py-4" : "px-3 py-2",
                    )}
                  >
                    <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Quick queries
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {quickActions.map((action) => (
                        <button
                          key={action.label}
                          onClick={() => handleSend(action.query)}
                          className="flex items-center gap-1.5 rounded-full bg-lime-100/10 px-2.5 py-1 text-[12px] font-medium text-lime-100 transition-colors hover:bg-lime-100/20"
                        >
                          <action.icon className="h-3 w-3" />
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Messages */}
                <div
                  className={clsx(
                    `overflow-y-auto bg-muted/30`,
                    isExpanded ? "px-6 py-4 h-[60vh]" : "h-[420px] p-4",
                  )}
                >
                  <div className="space-y-8 h-full pb-4">
                    {!messages?.length && !activeThread && (
                      <div className="flex flex-col gap-3 items-center justify-center text-center h-full text-muted-foreground font-bold">
                        <div
                          className={cn(
                            "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                            "bg-gradient-to-br from-lime-100 to-[#0C3A04] text-primary-foreground",
                          )}
                        >
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <p>Hello {userName ?? ""}!</p>
                        <p>I'm your AI assistant. How can I help you today?</p>
                      </div>
                    )}
                    {messages.map((message, index) => (
                      <div key={message.id} className={cn("flex gap-3")}>
                        <div
                          className={cn(
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                            message.role === "assistant"
                              ? "bg-gradient-to-br from-lime-100 to-[#0C3A04] text-primary-foreground"
                              : "bg-secondary text-secondary-foreground",
                          )}
                        >
                          {message.role === "assistant" ? (
                            <Sparkles className="h-4 w-4" />
                          ) : (
                            <User className="h-4 w-4" />
                          )}
                        </div>
                        {streamState.isStreaming &&
                        message.content === "" &&
                        index === messages.length - 1 ? (
                          <div className="rounded-2xl bg-card px-4 py-3 shadow-sm">
                            <div className="flex gap-1">
                              <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:-0.3s]" />
                              <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:-0.15s]" />
                              <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" />
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1 w-full">
                            <div
                              className={cn(
                                "rounded-2xl px-2 pt-1.5 pb-2.5",
                                "text-card-foreground overflow-hidden",
                                isExpanded ? "max-w-[100%]" : "max-w-[100%]",
                              )}
                            >
                              <div className="prose prose-sm md:prose-base prose-lime max-w-none break-words">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {message.content}
                                </ReactMarkdown>
                              </div>
                            </div>

                            {/* Action buttons for assistant messages */}
                            {message.role === "assistant" && index > 0 && (
                              <div className="flex items-center gap-1">
                                {!streamState.isStreaming ||
                                index !== messages?.length - 1 ? (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() =>
                                      handleCopy(message.content, message.id)
                                    }
                                    className="h-7 w-7 rounded text-muted-foreground/60 hover:text-foreground"
                                  >
                                    {copiedId === message.id ? (
                                      <Check className="h-5 w-5 text-primary" />
                                    ) : (
                                      <Copy className="h-5 w-5" />
                                    )}
                                    <span className="sr-only">
                                      Copy message
                                    </span>
                                  </Button>
                                ) : undefined}
                                {message.id === lastAssistantMessageId &&
                                  !streamState.isStreaming && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() =>
                                        handleRegenerate(message.id)
                                      }
                                      className="h-7 w-7 rounded text-muted-foreground/60 hover:text-foreground"
                                    >
                                      <RotateCcw className="h-5 w-5" />
                                      <span className="sr-only">
                                        Regenerate response
                                      </span>
                                    </Button>
                                  )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Input */}
                <div
                  className={clsx(
                    "border-t bg-background bottom-0",
                    isExpanded ? "px-6 py-4" : "p-3",
                  )}
                >
                  <div className="flex flex-col items-end rounded-xl border border-muted-foreground/20 bg-muted/30 px-1 py-1">
                    <Textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="What can I answer for you ?"
                      className="flex-1 border-0 bg-transparent px-2 shadow-none focus-visible:ring-0 resize-none h-24"
                      disabled={streamState.isStreaming}
                    />
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-medium text-muted-foreground">
                        SSA Climate AI Model
                      </span>
                      {streamState?.isStreaming ? (
                        <Button
                          size="icon"
                          variant={"ghost"}
                          onClick={streamState?.stopStream}
                          disabled={!streamState.isStreaming}
                          className="h-9 w-9 shrink-0 rounded-lg hover:bg-gray-200/90"
                        >
                          <Square className="h-6 w-6 text-gray-500" />
                          <span className="sr-only">
                            Stop response streaming
                          </span>
                        </Button>
                      ) : (
                        <Button
                          size="icon"
                          variant={"ghost"}
                          onClick={() => handleSend()}
                          disabled={!input.trim() || streamState.isStreaming}
                          className="h-9 w-9 shrink-0 rounded-lg hover:bg-gray-200/90"
                        >
                          <Send className="h-6 w-6 text-lime-100" />
                          <span className="sr-only">Send message</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground/60">
                      AI can make mistakes. Verify important info.
                    </span>
                  </div>
                </div>
              </div>
            ) : undefined}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AgentButton;
